import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageHelpButton } from '@/src/renderer/components/common/PageHelpButton'
import { useScrollbarVisibility } from '@/src/renderer/hooks/useScrollbarVisibility'
import { useTranslation } from 'react-i18next'
import { DormantOpenIcon } from '../../../../components/icons/DormantIcon'
import { useDataSource } from '../../../../lib/dataSource'
import { useSoftAddSound } from '../../../../lib/sounds'
import { EmptyState } from '../../components/EmptyState'
import { TaskList, type TaskListHandle, type TaskListSelection } from '../../components/TaskList'
import type { TaskEntryData } from '../../components/TaskEntry'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useContextMenuMarks } from '../../hooks/useContextMenuMarks'
import { useTaskMenuShortcuts, anchorTaskId } from '../../hooks/useTaskMenuShortcuts'
import { useAppTaskMenu, menuAnchorOf, useShowTaskInProject } from '../../hooks/useAppTaskMenu'
import { MoveMenuContext } from '../../utils/moveMenu'
import { useMoveMenu } from '../../hooks/useMoveMenu'
import { useFavoriteShortcut } from '../../hooks/useFavoriteShortcut'
import { usePendingPageJump } from '../../hooks/usePendingPageJump'
import type { TaskMenuTarget } from '../../components/TaskMenu/TaskMenu.types'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import { duplicateTask } from '../../utils/duplicateTask'
import { orderAtEndOfOwnPlace } from '../../utils/appendTaskOrders'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import { taskToEntryData } from '../../utils/taskToEntryData'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import { useCompletionGrace } from '../../hooks/useCompletionGrace'
import { splitCompleted } from '../../utils/completedGate'
import { useDormantTasks } from './hooks/useDormantTasks'
import '../TaskViews.scss'
import './DormantPage.scss'
import { usePlayTask } from '@/src/renderer/lib/focus'
import { CalmIllustration } from '../../components/CalmIllustration'
import { useStableRandomIndex } from '../../hooks/useStableRandomIndex'
import { usePageBreadcrumb } from '../../hooks/usePageBreadcrumb'
import { useScrollFades } from '../../hooks/useScrollFades'

/** So viele Varianten stehen unter `…emptyTitles` in den Sprachdateien. */
const EMPTY_TITLE_COUNT = 9

interface DormantPageProps {
  lifeSpace: 'personal' | 'work' | 'home'
}

export const DormantPage: React.FC<DormantPageProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  // Neun Titel für den leeren Zustand, einer pro Besuch (24.09.2026).
  const emptyTitleIndex = useStableRandomIndex(EMPTY_TITLE_COUNT)
  const isActive = useIsActiveTab()
  const rawTasks = useDormantTasks({ lifeSpace, isActive })
  const scrollRef = useRef<HTMLDivElement>(null)
  // Die **Bildlaufleiste** (CONTEXT.md): unsichtbar in Ruhe, sichtbar beim
  // Scrollen, wachgehalten von der Mausbewegung.
  const { visible: scrollbarVisible, onScroll: handleScrollbar } =
    useScrollbarVisibility({ ref: scrollRef })
  // Die Spalte um den Scroller trägt die Verläufe oben und unten — wie Projekt,
  // Geschafft und Kurz notiert (#273).
  const listColumnRef = useRef<HTMLDivElement>(null)
  useScrollFades(scrollRef, listColumnRef)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const taskListRef = useRef<TaskListHandle | null>(null)
  /** **Play** — beginnt einen Fokus an dieser Aufgabe (Zeile, Menü). */
  const handlePlayTask = usePlayTask()
  const playSoftAdd = useSoftAddSound()
  const dataSource = useDataSource()

  // Optimistic overrides so toggle/edit/badge-update paint in the next frame
  // instead of waiting on the IDB write + LiveQuery refire.
  const { optimisticTaskOverrides, setOptimisticTaskOverride } = useOptimisticTaskOverrides(rawTasks)

  // Merge overrides and drop rows that optimistically left the Ruhend view
  // (un-paused or soft-deleted).
  //
  // **Erledigte bleiben in der Abfrage, aber nicht auf der Seite**: Wer hier abhakt,
  // sieht die Zeile die **Liegezeit** über stehen (Rücknahme mit demselben Klick) und
  // danach gehen. Einen **Geschafft-Schalter** hat Ruhend NICHT (entschieden
  // 2026-09-12): Eine ruhende Aufgabe bleibt in ihrer Projektliste stehen, ihr
  // Erledigtes liegt also schon hinter DEREN Schalter — hier wäre es eine
  // Zweitansicht. Was kein Projekt hat, findet sich auf **Geschafft**.
  const effectiveTasks = useMemo(() => {
    if (!rawTasks) return rawTasks
    if (optimisticTaskOverrides.size === 0) return rawTasks
    return rawTasks.flatMap(task => {
      const override = optimisticTaskOverrides.get(task.id)
      const merged = override ? { ...task, ...override } : task
      if (merged.deletedAt) return []
      if (merged.pausedAt == null) return []
      return [merged]
    })
  }, [rawTasks, optimisticTaskOverrides])

  // ── Die Liegezeit ──
  // Auf Ruhend wird nicht gezogen, also braucht sie hier kein `hold`.
  const { graceIds, markCompleted } = useCompletionGrace()
  const { visible: visibleTasks } = useMemo(
    () => splitCompleted(effectiveTasks, graceIds),
    [effectiveTasks, graceIds],
  )

  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  // Jeder Weg zum Haken läuft hier durch — Klick aufs Kästchen und
  // `Leertaste` auf der Auswahl (TaskList gibt beides als Set herein).
  const toggleCompleteWithGrace = useCallback((ids: Set<string>) => {
    markCompleted(ids)
    handleToggleComplete(ids)
  }, [markCompleted, handleToggleComplete])

  // `cloneTaskFields` preserves `pausedAt` by default, so a duplicate of a
  // dormant task lands right back in Ruhend without an extra update-write.
  const duplicateTasks = useCallback(async (ids: string[]) => {
    if (!rawTasks || ids.length === 0) return
    const byId = new Map(rawTasks.map(task => [task.id, task]))
    const newIds: string[] = []
    for (const id of ids) {
      const source = byId.get(id)
      if (!source) continue
      // Ans Ende ihres Abschnitts, nicht hinter die größte Nummer (Diff-Audit #276).
      const created = await duplicateTask(dataSource, source, { order: await orderAtEndOfOwnPlace(dataSource, source) })
      newIds.push(created.id)
    }
    newIds.forEach((_, i) => setTimeout(() => playSoftAdd(), i * 80))
    if (newIds.length > 0) taskListRef.current?.selectTasks(newIds)
  }, [rawTasks, playSoftAdd, dataSource])

  // Context-menu duplicate: if the clicked task is part of a multi-selection,
  // duplicate the whole selection; otherwise just the clicked task.
  const handleDuplicate = useCallback((clickedTaskId: string) => {
    const selection = taskListRef.current?.getSelection() ?? new Set<string>()
    const ids = selection.size > 1 && selection.has(clickedTaskId)
      ? Array.from(selection)
      : [clickedTaskId]
    void duplicateTasks(ids)
  }, [duplicateTasks])

  const handleContextMenuBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection: () => taskListRef.current?.getSelection() ?? new Set<string>(),
    setOptimisticTaskOverride,
    logTag: 'DormantPage',
  })

  /* Die Haken im **Aufgaben-Menü** — dieselbe Auswahl wie oben. */
  const getContextMenuMarks = useContextMenuMarks({
    getSelection: () => taskListRef.current?.getSelection() ?? new Set<string>(),
    tasks: rawTasks ?? [],
  })

  /*
    **Die Kürzel des Aufgaben-Menüs** — auch hier. Auf **Ruhend** gibt es nichts
    anzulegen, wohl aber eine Aufgabe zu bearbeiten; deshalb der eigene Hook und
    nicht der Projekt-Hook mit seinen Anlege-Tasten.

    Der Anker kommt aus dem Griff der Liste: Sie führt hier ihre eigene
    Navigation, es gibt keine Seiten-weite.
  */
  const [menuShortcut, setMenuShortcut] = useState<{ taskId: string; target: TaskMenuTarget } | null>(null)
  const closeMenuShortcut = useCallback(() => setMenuShortcut(null), [])
  // „Verschieben …“ aus dem Rechtsklick öffnet denselben Kasten an der Zeile wie ⇧⌘M (#263).
  const openMoveAtRow = useCallback((taskId: string) => setMenuShortcut({ taskId, target: 'move' }), [])

  const handleRestShortcut = useCallback((taskId: string) => {
    const task = rawTasks?.find(entry => entry.id === taskId)
    if (!task) return
    handleContextMenuBadgeUpdate(taskId, task.pausedAt ? wakeTaskUpdate(task, new Date()) : { pausedAt: new Date() })
  }, [rawTasks, handleContextMenuBadgeUpdate])

  // ⇧⌘F (#210): der Stern der Auswahl — mit Meldung und Rückgängig.
  // Klick auf eine ruhende Aufgabe ohne Projekt in „Favoriten“ (#210).
  usePendingPageJump({
    page: 'dormant', lifeSpace, isActive, ready: rawTasks !== undefined, rootRef: scrollRef,
    select: taskId => taskListRef.current?.selectTask(taskId),
  })
  const favoriteShortcut = useFavoriteShortcut({ getSelection: () => taskListRef.current?.getSelection() ?? new Set<string>(), applyUpdate: handleContextMenuBadgeUpdate })
  const showTaskInProject = useShowTaskInProject()
  useTaskMenuShortcuts({
    enabled: isActive,
    getAnchorTaskId: () => anchorTaskId(
      taskListRef.current?.getFocusedTaskId() ?? null,
      (rawTasks ?? []).map(task => task.id),
      taskListRef.current?.getSelection(),
    ),
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onFavoriteShortcut: taskId => { if (taskId) void favoriteShortcut(taskId) },
    onRestShortcut: handleRestShortcut,
    onFocusShortcut: handlePlayTask,
    onShowInProjectShortcut: showTaskInProject,
    canMove: true,
  })

  // **Verschieben …** (#257): Rechtsklick, ⇧⌘M und das Menü „Aufgaben“. Die Aufgabe ruht danach weiter.
  const moveMenu = useMoveMenu(() => taskListRef.current?.getSelection() ?? new Set<string>(), openMoveAtRow)

  /*
    **Das Menü „Aufgaben“ in der Menüleiste** (#257). Die Liste meldet ihre Auswahl — das Menü
    muss sie kennen, bevor jemand es aufklappt; die Griffe oben lesen sie nur auf Abruf.
  */
  const [listSelection, setListSelection] = useState<TaskListSelection>(NO_SELECTION)
  const menuAnchor = useMemo(() => {
    const id = anchorTaskId(listSelection.focusedTaskId, (visibleTasks ?? []).map(task => task.id), listSelection.selectedIds)
    return menuAnchorOf(id ? effectiveTasks?.find(task => task.id === id) : null)
  }, [listSelection, visibleTasks, effectiveTasks])
  useAppTaskMenu({
    enabled: isActive,
    anchor: menuAnchor,
    multiSelected: listSelection.selectedIds.size > 1,
    canMove: true,
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onComplete: () => taskListRef.current?.completeSelection(),
    onFocus: handlePlayTask,
    onRest: handleRestShortcut,
    onFavorite: taskId => { void favoriteShortcut(taskId) },
    onShowInProject: showTaskInProject,
    onDuplicate: () => { if (menuAnchor) handleDuplicate(menuAnchor.id) },
    onDelete: () => taskListRef.current?.deleteSelection(),
  })

  // Look up project names so the inline ProjectBadge in each row can show
  // the parent project. Independent read (vs. piggy-backing on
  // `useDormantTasks`) keeps the hook's contract focused on tasks.
  const projects = dataSource.useLifeSpaceProjects(lifeSpace)
  const projectNameMap = useMemo(() => {
    const map = new Map<string, { name: string; icon: string | null; iconColor: string | null }>()
    projects?.forEach(p => map.set(p.id, { name: p.name, icon: p.icon ?? null, iconColor: p.iconColor ?? null }))
    return map
  }, [projects])

  const taskEntries = useMemo<TaskEntryData[]>(
    () => visibleTasks
      ? visibleTasks.map(task => {
          const entry = taskToEntryData(task)
          if (!task.projectId) return entry
          const project = projectNameMap.get(task.projectId)
          return project
            ? { ...entry, projectName: project.name, projectIcon: project.icon, projectIconColor: project.iconColor }
            : entry
        })
      : [],
    [visibleTasks, projectNameMap],
  )

  // Re-focus the list whenever this page becomes active. TabStack's
  // `display: none` on inactive slots drops focus to <body>, so without
  // this the user would have to click the list once after every tab
  // switch to get ArrowKey/Delete back. rAF defers past the tab-swap
  // paint, and we skip the refocus when the user is mid-typing somewhere
  // else (e.g. inline task edit) so focus doesn't yank out from under them.
  useEffect(() => {
    if (!isActive) return
    const rafId = requestAnimationFrame(() => {
      const active = document.activeElement
      const typingTag = active?.tagName
      if (typingTag === 'INPUT' || typingTag === 'TEXTAREA') return
      taskListRef.current?.focusList()
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive])

  // Erst mit Daten ist „leer“ eine Aussage — vor der Lade-Rückkehr, Hooks stehen nicht bedingt.
  const isEmpty = rawTasks !== undefined && taskEntries.length === 0
  // Der Seitenname in der Breadcrumb: leer fest, sonst sobald die Überschrift weggerollt ist.
  usePageBreadcrumb({
    title: t('dormant.title'), isActive, loaded: rawTasks !== undefined, isEmpty, titleRef, scrollerRef: scrollRef,
  })

  if (rawTasks === undefined) {
    return <div className="task-view dormant-page" aria-busy="true" />
  }


  return (
    <MoveMenuContext.Provider value={moveMenu}>
    <div className="task-view dormant-page">
      <div ref={listColumnRef} className="dormant-page__list-column">
      <div
        className={`dormant-page__scroll${scrollbarVisible ? ' dormant-page__scroll--scroll-visible' : ''}${isEmpty ? ' dormant-page__scroll--empty' : ''}`}
        ref={scrollRef}
        onScroll={handleScrollbar}
      >
        {/* Im Scroller, nicht darüber: Die Überschrift rollt mit der Liste weg — wie im
            Projekt (#273). */}
        {!isEmpty && (
          <div className="dormant-page__header">
            <div className="page-help__row">
              <h1 ref={titleRef} className="dormant-page__title">
                <DormantOpenIcon size={18} className="dormant-page__title-icon" />
                {t('dormant.title')}
              </h1>
              <PageHelpButton page="dormant" />
            </div>
            <p className="dormant-page__subtitle">{t('dormant.subtitle')}</p>
          </div>
        )}
        {isEmpty ? (
          <EmptyState
            illustration={<CalmIllustration />}
            title={t(`dormant.emptyTitles.${emptyTitleIndex}`)}
            text={t('dormant.emptyText')}
            titleAside={<PageHelpButton page="dormant" />}
          />
        ) : (
          <TaskList
            tasks={taskEntries}
            handleRef={taskListRef}
            onSelectionChange={setListSelection}
            stateKey={`dormant-${lifeSpace}`}
            onToggleComplete={toggleCompleteWithGrace}
            onDelete={handleDeleteTask}
            onDuplicate={handleDuplicate}
            onShowInProject={showTaskInProject}
            onContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
            getContextMenuMarks={getContextMenuMarks}
            menuShortcut={menuShortcut}
            onPlay={handlePlayTask}
            onMenuShortcutClose={closeMenuShortcut}
            onUpdateTask={handleUpdateTask}
          />
        )}
      </div>
      </div>
    </div>
    </MoveMenuContext.Provider>
  )
}

const NO_SELECTION: TaskListSelection = { selectedIds: new Set(), focusedTaskId: null }
